import { AsyncPipe, CommonModule, NgIf } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterEvent,
  RouterOutlet,
} from '@angular/router';
import { CmsContentDirective, CmsFooterComponent } from '@domgen/dgx-fe-cms';
import { ModalContainerComponent } from '@domgen/dgx-fe-components/modal';
import { NotificationsComponent } from '@domgen/dgx-fe-notifications';
import { BreadcrumbSearchBarComponent } from '@self-fix/feature-components';
import { SelfFixRoutes } from '@self-fix/util-models';
import { AppComponentService } from '../services/app-component/app-component.service';
import { HeaderComponent } from '@domgen/dgx-fe-components/layout';
import { AspectRatio, Picture } from '@domgen/dgx-fe-components/media';
import { NavigationLink } from '@domgen/dgx-fe-common';
import { AppConfig } from '../models/config/app-config.type';
import { CONFIG, ENVIRONMENT } from '@domgen/dgx-fe-config';
import { HubPageWrapperComponent } from 'libs/self-fix/feature-components/src/lib/components/hero-page-component/hub-page-wrapper.component';
import { LoadingOverlayComponent } from '@domgen/dgx-fe-components/loading';
import { SmartFixHubFooterComponent } from 'libs/self-fix/feature-components/src/lib/components/footer/footer.component';
import {
  MyAccountService,
  PlanClaimsStore,
  SmartFixHubAnalyticsService,
} from '@self-fix/data-access';
import { BehaviorSubject, take } from 'rxjs';
import { AppEnvironment } from '../models/environment/app-environment.type';
import { GridContainerComponent } from '@domgen/dgx-fe-components/grid';
@Component({
  selector: 'dg-self-fix-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [
    AsyncPipe,
    BreadcrumbSearchBarComponent,
    CmsContentDirective,
    CmsFooterComponent,
    ModalContainerComponent,
    NgIf,
    GridContainerComponent,

    NotificationsComponent,
    RouterOutlet,
    HeaderComponent,
    CommonModule,
    HubPageWrapperComponent,
    LoadingOverlayComponent,
    SmartFixHubFooterComponent,
  ],
})
export class AppComponent implements OnInit {
  navigationLink = { href: window.location.origin };
  showBreadcrumbs = false;
  showStepper = false;
  routesWithoutBreadcrumbs = [
    SelfFixRoutes.ApplianceDetails,
    SelfFixRoutes.ApplianceDetails + '#1',
    SelfFixRoutes.ApplianceDetails + '#2',
    SelfFixRoutes.ApplianceDetails + '#3',
  ];

  clientLogo: Picture = {
    sourceSet: {},
    alt: this.environment.brandConfig.logo.alt,
    aspectRatio: this.environment.brandConfig.logo.aspectRatio as AspectRatio,
    src: this.environment.brandConfig.logo.src,
  };

  navigationLinks: NavigationLink[] = [];

  stepperRoutes = [
    SelfFixRoutes.ArticlesTroubleshooting,
    SelfFixRoutes.ArticlesHowToFix,
  ];

  loggingOut = false;

  loading$ = new BehaviorSubject<boolean>(true);

  brandThemeClass = this.environment.brandKey + '-theme';
  notificationsEnabled = this.environment.hasMyAccountIntegration;

  constructor(
    readonly cs: AppComponentService,
    readonly router: Router,
    readonly myAccountService: MyAccountService,
    @Inject(CONFIG) private readonly config: AppConfig,
    private smartFixHubAnalytics: SmartFixHubAnalyticsService,
    private planStore: PlanClaimsStore,
    @Inject(ENVIRONMENT) private readonly environment: AppEnvironment
  ) {
    router.events
      .pipe(takeUntilDestroyed())
      .subscribe((val: NavigationStart | NavigationEnd) => {
        this.navigationInterceptor(val);
        if (val instanceof NavigationEnd) {
          this.showBreadcrumbs = !this.routesWithoutBreadcrumbs.includes(
            val.url.split('?')[0]
          );
          this.showStepper =
            this.stepperRoutes.some((route) =>
              val.urlAfterRedirects.startsWith(route)
            ) || false;
        }
      });
  }

  // Shows and hides the loading spinner during RouterEvent changes
  navigationInterceptor(event: RouterEvent): void {
    if (event instanceof NavigationStart) {
      this.loading$.next(true);
    }
    if (event instanceof NavigationEnd) {
      this.loading$.next(false);
    }

    // Set loading state to false in both of the below events to hide the spinner in case a request fails
    if (event instanceof NavigationCancel) {
      this.loading$.next(false);
    }
    if (event instanceof NavigationError) {
      this.loading$.next(false);
    }
  }

  ngOnInit(): void {
    if (this.environment.hasMyAccountIntegration) {
      this.navigationLinks = [
        {
          label: 'Dashboard',
          href: this.config.plansHomePage,
        },
        {
          label: 'Your details',
          href: this.config.personalDetailsPage,
        },
        {
          label: 'Your preferences',
          href: this.config.myPreferencesPage,
        },
        {
          label: 'Smart-fix hub',
          routerPath: '/',
          isActivePath: true,
        },
        {
          label: 'Logout',
          newWindow: false,
          icon: 'arrow-right-from-bracket',
          href: this.config.myAccountLogoutPage,
        },
      ];
    } else {
      this.navigationLinks = [
        {
          label: 'Home',
          routerPath: '/',
          isActivePath: true,
        },
      ];
    }
  }

  // need this to know which label was selected in the nav in the shared component
  headerNavClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const clickedElement = target.closest('a');
    const matchedLink = this.navigationLinks.find((link: any) =>
      clickedElement?.textContent?.trim().includes(link?.label)
    );

    if (matchedLink && matchedLink.label === 'Logout') {
      event.preventDefault();
      this.loggingOut = true;
      this.myAccountService
        .callSignout()
        .pipe(take(1))
        .subscribe(() => {
          this.myAccountService.redirectToLogin();
        });
    }

    if (matchedLink) {
      this.smartFixHubAnalytics.trackEvent({
        eventAction: 'top bar nav',
        eventCategory: 'navigation menu',
        eventLabel: matchedLink.label,
        pagename: 'appliance-details',
        event: 'genericGAEvent',
      });
    }
  }

  isScottishPower(): boolean {
    return this.environment.brandKey === 'scottish';
  }
}
